import React, { MouseEvent, ReactNode, useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";

const ToggleWrapper = styled.label.attrs({
  className: "u-w100 u-row u-gap8 u-relative u-align-center",
})`
  cursor: pointer;
`;

const bounceOn = keyframes`
  0% { transform: translateX(0); }
  50% { transform: translateX(28px); }
  70% { transform: translateX(22px); }
  100% { transform: translateX(24px); }
`;

const bounceOff = keyframes`
  0% { transform: translateX(24px); }
  50% { transform: translateX(-4px); }
  70% { transform: translateX(2px); }
  100% { transform: translateX(0); }
`;

const ToggleTrack = styled.div<{ $isOn: boolean }>`
  position: relative;
  width: 52px;
  min-width: 52px;
  height: 28px;
  background-color: ${({ $isOn }) => ($isOn ? "var(--soremo-blue)" : "#ccc")};
  border-radius: 14px;
  transition: background-color 0.25s ease;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  padding: 2px;
`;

const ToggleKnob = styled.div<{ $isOn: boolean; $hasAnimated: boolean }>`
  width: 24px;
  height: 24px;
  background-color: white;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  animation: ${({ $isOn, $hasAnimated }) =>
      $isOn
        ? $hasAnimated
          ? bounceOn
          : "none"
        : $hasAnimated
          ? bounceOff
          : "none"}
    0.3s ease forwards;
  transform: translateX(${({ $isOn }) => ($isOn ? "24px" : "0")});
`;

export interface ToggleProps {
  value?: boolean;
  children?: ReactNode;
  onClick?: (e: MouseEvent<HTMLLabelElement>) => void;
}

const Toggle: React.FC<ToggleProps> = ({ value = true, children, onClick }) => {
  const [hasAnimated, setHasAnimated] = useState(false);
  const prevValueRef = useRef(value);

  useEffect(() => {
    if (prevValueRef.current !== value) {
      setHasAnimated(true);
      prevValueRef.current = value;
    }
  }, [value]);

  return (
    <ToggleWrapper onClick={onClick}>
      <ToggleTrack $isOn={value}>
        <ToggleKnob $isOn={value} $hasAnimated={hasAnimated} />
      </ToggleTrack>
      <div>{children}</div>
    </ToggleWrapper>
  );
};

export default Toggle;
